import React from 'react';
import { Users, UserCheck, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SimultaneousAttendanceDisplayProps {
  currentlyServing: number;
  maxSimultaneous: number;
  servingEntries?: Array<{
    userId: string;
    position: number;
    participantName?: string;
  }>;
  estimatedWaitTime?: number;
  isYourTurn?: boolean;
  className?: string;
}

export const SimultaneousAttendanceDisplay: React.FC<SimultaneousAttendanceDisplayProps> = ({
  currentlyServing,
  maxSimultaneous,
  servingEntries = [],
  estimatedWaitTime,
  isYourTurn = false,
  className,
}) => {
  const availableSlots = maxSimultaneous - currentlyServing;
  const percentage = (currentlyServing / maxSimultaneous) * 100;

  return (
    <div className={cn("space-y-4", className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium flex items-center gap-2">
          <Users className="w-5 h-5" />
          Atendimento Simultâneo
        </h3>
        {maxSimultaneous > 1 && (
          <span className="text-sm text-muted-foreground">
            Até {maxSimultaneous} pessoas por vez
          </span>
        )}
      </div>

      {/* Visual Indicator */}
      {maxSimultaneous > 1 && (
        <div className="bg-card rounded-ds-card p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">
              {currentlyServing}/{maxSimultaneous} sendo atendidos
            </span>
            {availableSlots > 0 && (
              <span className="text-sm text-status-ok">
                {availableSlots} {availableSlots === 1 ? 'vaga' : 'vagas'} disponível
              </span>
            )}
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-muted rounded-full h-3 overflow-hidden">
            <div
              className={cn(
                "h-full rounded-full transition-all duration-500",
                percentage === 100 ? "bg-brand-danger" : "bg-primary"
              )}
              style={{ width: `${percentage}%` }}
            />
          </div>

          {/* Slots Visual */}
          <div className="grid grid-cols-5 gap-2 mt-4">
            {Array.from({ length: maxSimultaneous }).map((_, index) => (
              <div
                key={index}
                className={cn(
                  "aspect-square rounded-ds-card flex items-center justify-center transition-colors",
                  index < currentlyServing
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground"
                )}
              >
                {index < currentlyServing ? (
                  <UserCheck className="w-4 h-4" />
                ) : (
                  <Users className="w-4 h-4" />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Currently Being Served List */}
      {servingEntries.length > 0 && (
        <div className="border rounded-ds-card p-4">
          <h4 className="text-sm font-medium mb-3">Sendo atendidos agora:</h4>
          <div className="space-y-2">
            {servingEntries.map((entry) => (
              <div
                key={entry.userId}
                className="flex items-center justify-between p-2 bg-primary/10 rounded"
              >
                <span className="text-sm">
                  Posição #{entry.position}
                  {entry.participantName && ` - ${entry.participantName}`}
                </span>
                <span className="text-xs text-primary font-medium">
                  Em atendimento
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Your Turn Alert */}
      {isYourTurn && (
        <div className="bg-brand-green/10 border border-brand-green text-status-ok px-4 py-3 rounded-ds-card animate-pulse">
          <div className="flex items-center">
            <UserCheck className="w-5 h-5 mr-2" />
            <strong>É a sua vez!</strong>
          </div>
          <p className="text-sm mt-1">
            Dirija-se ao local de atendimento
            {maxSimultaneous > 1 && ` (${currentlyServing}/${maxSimultaneous} atendendo)`}
          </p>
        </div>
      )}

      {/* Wait Time Estimate */}
      {!isYourTurn && estimatedWaitTime && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Clock className="w-4 h-4" />
          <span>
            Tempo estimado de espera: {estimatedWaitTime} minutos
            {maxSimultaneous > 1 && (
              <span className="text-xs ml-1">
                (com {maxSimultaneous} atendimentos simultâneos)
              </span>
            )}
          </span>
        </div>
      )}

      {/* Info Box */}
      {maxSimultaneous > 1 && (
        <div className="bg-primary/10 border border-primary/30 rounded-ds-card p-3">
          <p className="text-xs text-primary">
            <strong>Como funciona:</strong> Este atendimento permite que até{' '}
            {maxSimultaneous} pessoas sejam atendidas ao mesmo tempo, reduzindo
            significativamente o tempo de espera. Quando uma pessoa termina, a
            próxima da fila é chamada automaticamente.
          </p>
        </div>
      )}
    </div>
  );
};